import React from 'react';
import { Plane, Briefcase, Heart, MapPin, Check } from 'lucide-react';
import { Card } from './ui/card';
import { useLanguage } from '../contexts/LanguageContext';

const iconMap = {
  Plane,
  Briefcase,
  Heart,
  MapPin
};

const Services = () => {
  const { t } = useLanguage();
  
  const services = [
    {
      id: 1,
      title: t('services.service1.title'),
      description: t('services.service1.description'),
      icon: "Plane",
      image: "https://images.pexels.com/photos/4530180/pexels-photo-4530180.jpeg",
      features: t('services.service1.features')
    },
    {
      id: 2,
      title: t('services.service2.title'),
      description: t('services.service2.description'),
      icon: "Briefcase",
      image: "https://images.unsplash.com/photo-1530521954074-e64f6810b32d",
      features: t('services.service2.features')
    },
    {
      id: 3,
      title: t('services.service3.title'),
      description: t('services.service3.description'),
      icon: "Heart",
      image: "https://images.unsplash.com/photo-1698840059740-ba83e510733b",
      features: t('services.service3.features')
    },
    {
      id: 4,
      title: t('services.service4.title'),
      description: t('services.service4.description'),
      icon: "MapPin",
      image: "https://images.unsplash.com/photo-1645794225645-6fc366b25bbf",
      features: t('services.service4.features')
    }
  ];
  
  return (
    <section id="servizi" className="py-20 bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 scroll-mt-20">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block">
            <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider mb-2 block">
              {t('services.badge')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {t('services.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-amber-600 mx-auto"></div>
          </div>
          <p className="text-gray-400 text-lg mt-6 max-w-2xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card 
                key={service.id}
                className="group bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800 hover:border-amber-500/50 overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-amber-500/20 hover:-translate-y-2"
                style={{
                  animationDelay: `${index * 100}ms`
                }}
              >
                {/* Service Image */}
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/50 to-transparent"></div>
                  
                  {/* Icon Badge */}
                  <div className="absolute top-6 left-6 w-14 h-14 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-7 h-7 text-black" />
                  </div>
                </div>

                {/* Service Content */}
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-amber-400 transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features List */}
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-300">
                        <Check className="w-5 h-5 text-amber-400 mr-3 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
